import {
  Classes as BlueprintClasses,
  PopoverPosition as BlueprintPopoverPosition,
} from '@blueprintjs/core';
import {
  Add,
  ChevronRight,
  Close,
  WatsonHealthAiResultsLow,
} from '@carbon/icons-react';
import classNames from 'classnames';
import * as React from 'react';
import { v4 as uuid } from 'uuid';
import {
  Body,
  Button,
  Heading,
  Icon,
  LoadingIcon,
  OutlinedIconButton,
  StaticBadge,
  Textarea,
  Tooltip,
} from 'vet-bones/design-system/components';

import { Ai } from 'src/components/icons/Ai';
import { MAX_COMPETENCY_QUESTIONS } from 'src/constants/limits';
import { TEST_IDS } from 'src/constants/testIds';
import {
  CreateProjectStepValue,
  useCreateProjectState,
} from 'src/containers/CreateProjectOverlay/hooks/useCreateProjectState';
import * as copy from 'src/templates/copy';

export const CompetencyQuestionsPanel: React.VFC = () => {
  const {
    competencyQuestions,
    didGenerateInitialQuestions,
    isGeneratingQuestions,
    handleGenerateQuestions,
    setCompetencyQuestions,
    setCurrentStep,
  } = useCreateProjectState();

  const inputRefs = React.useRef<Map<number, HTMLTextAreaElement | null>>(
    new Map()
  );

  const questions = didGenerateInitialQuestions ? competencyQuestions : [];

  return (
    <div className="sd-competency-questions-panel">
      <div className="sd-competency-questions-panel-header">
        <div className="sd-create-project-panel-header-text">
          <Heading size="2xl">
            {
              copy.components.createProjectOverlay.competencyQuestionsPanel
                .title
            }
            <span>
              {
                copy.components.createProjectOverlay.competencyQuestionsPanel
                  .titleSpan
              }
            </span>
            {
              copy.components.createProjectOverlay.competencyQuestionsPanel
                .titleContinue
            }
          </Heading>

          <Body size="lg">
            {
              copy.components.createProjectOverlay.competencyQuestionsPanel
                .subtitle
            }
            <br />
            {
              copy.components.createProjectOverlay.competencyQuestionsPanel
                .subtitleSecond
            }
          </Body>
        </div>

        <div className="sd-competency-questions-panel-header-help">
          <div className="sd-competency-questions-panel-header-help-title">
            <Icon IconComponent={WatsonHealthAiResultsLow} size="md" />
            <Body size="xl">
              {
                copy.components.createProjectOverlay.competencyQuestionsPanel
                  .tipsTitle
              }
            </Body>
          </div>
          <div className="sd-competency-questions-panel-header-help-steps">
            {copy.components.createProjectOverlay.competencyQuestionsPanel.tips.map(
              (tip, tipIdx) => (
                <div
                  // eslint-disable-next-line react/no-array-index-key
                  key={tipIdx}
                  className="sd-competency-questions-panel-header-help-step"
                >
                  <StaticBadge size="xs" type="default" variant="fill">
                    {tipIdx + 1}
                  </StaticBadge>
                  <Body size="sm">{tip}</Body>
                </div>
              )
            )}
          </div>
        </div>
      </div>

      <div className="sd-competency-questions-panel-questions">
        <div className="sd-create-project-panel-list-title">
          <div className="sd-create-project-panel-list-title-text">
            <Body size="xl">
              {
                copy.components.createProjectOverlay.competencyQuestionsPanel
                  .myQuestions
              }
            </Body>
            {isGeneratingQuestions ? null : (
              <Body
                size="xl"
                testId={
                  TEST_IDS.createProjectOverlay.competencyQuestionsPanel
                    .questionsCount
                }
              >
                {questions.length}
              </Body>
            )}
            <Tooltip
              description={
                copy.components.createProjectOverlay.competencyQuestionsPanel
                  .generateQuestions
              }
              disabled={isGeneratingQuestions}
              position={BlueprintPopoverPosition.RIGHT}
            >
              <Button
                disabled={
                  isGeneratingQuestions ||
                  questions.length >= MAX_COMPETENCY_QUESTIONS
                }
                leftIcon={isGeneratingQuestions ? LoadingIcon : Ai}
                onClick={() => handleGenerateQuestions()}
                size="xs"
                testId={
                  TEST_IDS.createProjectOverlay.competencyQuestionsPanel
                    .generateQuestionsButton
                }
                variant="ghost-gray"
              />
            </Tooltip>
          </div>

          <div className="sd-create-project-panel-list-title-actions">
            {questions.length ? (
              <OutlinedIconButton
                elevated
                leftIcon={Close}
                onClick={() =>
                  setCompetencyQuestions([{ id: uuid(), question: '' }])
                }
                testId={
                  TEST_IDS.createProjectOverlay.competencyQuestionsPanel
                    .clearAllButton
                }
              />
            ) : null}
          </div>
        </div>

        <div className="sd-competency-questions-panel-questions-scroll">
          <div
            key={questions.length}
            className="sd-competency-questions-panel-questions-list"
          >
            {questions.map((question, questionIdx) => (
              <Textarea
                key={question.id}
                actions={
                  <OutlinedIconButton
                    elevated
                    leftIcon={Close}
                    onClick={() =>
                      setCompetencyQuestions((prevQuestions) => {
                        if (prevQuestions.length <= 1) {
                          return [{ id: uuid(), question: '' }];
                        }

                        const updatedQuestions = [...prevQuestions];
                        updatedQuestions.splice(questionIdx, 1);
                        return updatedQuestions;
                      })
                    }
                    testId={
                      TEST_IDS.createProjectOverlay.competencyQuestionsPanel
                        .questionRemoveButton
                    }
                  />
                }
                autoFocus={Boolean(
                  !question.isGenerated
                    ? questionIdx === questions.length - 1
                    : null
                )}
                className={classNames(
                  'sd-competency-questions-panel-question',
                  {
                    'sd-competency-questions-panel-question--generated':
                      question.isGenerated,
                  }
                )}
                growVertically={Boolean(question.question)}
                id={`competency-question-${questionIdx}`}
                inputRef={(el) => inputRefs.current.set(questionIdx, el)}
                label=""
                onChange={(evt) => {
                  if (
                    evt.target.value === '\n' ||
                    // only happens when 20 question limit is reached
                    // since otherwise the focus goes to new question
                    evt.target.value === `${question.question}\n`
                  ) {
                    return;
                  }
                  setCompetencyQuestions((prevQuestions) => {
                    const updatedQuestions = [...prevQuestions];
                    updatedQuestions.splice(questionIdx, 1, {
                      id: question.id,
                      question: evt.target.value,
                      isGenerated: false,
                    });
                    return updatedQuestions;
                  });
                }}
                onKeyDown={(evt) => {
                  if (evt.key === 'Backspace' && !question.question) {
                    setCompetencyQuestions((prevQuestions) => {
                      const updatedQuestions = [...prevQuestions];
                      updatedQuestions.splice(questionIdx, 1);
                      return updatedQuestions;
                    });
                    inputRefs.current.get(questionIdx - 1)?.focus();
                  } else if (evt.key === 'Enter' && question.question) {
                    const nextEmptyIdx = questions.findIndex(
                      (q, idx) => !q.question && idx > questionIdx
                    );
                    if (nextEmptyIdx !== -1) {
                      inputRefs.current.get(nextEmptyIdx)?.focus();
                    } else {
                      setCompetencyQuestions((prevQuestions) => [
                        ...prevQuestions,
                        { id: uuid(), question: '' },
                      ]);
                    }
                  }
                }}
                onPaste={(evt) => {
                  const pasted = evt.clipboardData
                    .getData('text')
                    .split('\n')
                    .map((line) => line.trim())
                    .filter((line) => line);
                  if (pasted.length > 1) {
                    evt.preventDefault();
                    setCompetencyQuestions((prevQuestions) => {
                      const updatedQuestions = [...prevQuestions];
                      const pastedQuestions = pasted.map((question) => ({
                        id: uuid(),
                        question,
                      }));
                      if (updatedQuestions[questionIdx].question) {
                        updatedQuestions.splice(
                          questionIdx + 1,
                          0,
                          ...pastedQuestions
                        );
                      } else {
                        updatedQuestions.splice(
                          questionIdx,
                          1,
                          ...pastedQuestions
                        );
                      }
                      return updatedQuestions;
                    });
                  }
                }}
                placeholder={
                  copy.components.createProjectOverlay.competencyQuestionsPanel
                    .questionPlaceholder
                }
                rows={1}
                testId={TEST_IDS.createProjectOverlay.competencyQuestionsPanel.questionInput(
                  question.id,
                  questionIdx
                )}
                value={question.question}
              />
            ))}
            {isGeneratingQuestions ? (
              <>
                <div
                  className={classNames(
                    'sd-competency-questions-panel-question--skeleton',
                    BlueprintClasses.SKELETON
                  )}
                />
                <div
                  className={classNames(
                    'sd-competency-questions-panel-question--skeleton',
                    BlueprintClasses.SKELETON
                  )}
                />
                <div
                  className={classNames(
                    'sd-competency-questions-panel-question--skeleton',
                    BlueprintClasses.SKELETON
                  )}
                />
              </>
            ) : null}
          </div>
        </div>

        <div className="sd-create-project-panel-footer">
          <div className="sd-create-project-panel-footer-actions">
            <Tooltip
              description={
                copy.components.createProjectOverlay.competencyQuestionsPanel
                  .addQuestionDisabled
              }
              disabled={
                isGeneratingQuestions ||
                questions.length < MAX_COMPETENCY_QUESTIONS
              }
            >
              <Button
                disabled={
                  isGeneratingQuestions ||
                  questions.length >= MAX_COMPETENCY_QUESTIONS
                }
                onClick={() =>
                  setCompetencyQuestions((prevQuestions) => [
                    ...prevQuestions,
                    { id: uuid(), question: '' },
                  ])
                }
                rightIcon={Add}
                size="lg"
                testId={
                  TEST_IDS.createProjectOverlay.competencyQuestionsPanel
                    .addQuestionButton
                }
                variant="tertiary-gray"
              >
                {
                  copy.components.createProjectOverlay.competencyQuestionsPanel
                    .addQuestion
                }
              </Button>
            </Tooltip>
          </div>

          <div className="sd-create-project-panel-footer-actions">
            <Button
              disabled={isGeneratingQuestions}
              onClick={() =>
                setCurrentStep(CreateProjectStepValue.DATA_CONNECTION)
              }
              rightIcon={ChevronRight}
              size="lg"
              testId={
                TEST_IDS.createProjectOverlay.competencyQuestionsPanel
                  .submitButton
              }
              variant="primary-accent"
            >
              {copy.components.createProjectOverlay.steps.dataConnection}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
